
import React, { useState } from "react";
import FeedbackList from "./FeedbackList";
import FeedbackSearch from "./FeedbackSearch";
import FeedbackStatusFilter from "./FeedbackStatusFilter";
import FeedbackSortOptions from "./FeedbackSortOptions";
import BoardSelector from "./BoardSelector";
import { type Feedback } from "@/types";

type FeedbackContainerProps = {
  feedback: Feedback[];
  companyStatuses?: any[];
  onVote: (feedbackId: string) => void;
  companyId: string;
};

export default function FeedbackContainer({
  feedback,
  companyStatuses = [],
  onVote,
  companyId,
}: FeedbackContainerProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [sortOption, setSortOption] = useState("newest");
  const [selectedBoardId, setSelectedBoardId] = useState<string | null>(null);

  // Filter feedback based on search, status, and board
  const filteredFeedback = feedback.filter((item) => {
    const matchesSearch =
      searchQuery === "" ||
      item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (item.description &&
        item.description.toLowerCase().includes(searchQuery.toLowerCase()));

    const matchesStatus =
      selectedStatuses.length === 0 || selectedStatuses.includes(item.status);

    const matchesBoard =
      selectedBoardId === null || item.board_id === selectedBoardId;

    return matchesSearch && matchesStatus && matchesBoard;
  });

  // Sort feedback
  const sortedFeedback = [...filteredFeedback].sort((a, b) => {
    switch (sortOption) {
      case "newest":
        return new Date(b.created_at || "").getTime() - new Date(a.created_at || "").getTime();
      case "oldest":
        return new Date(a.created_at || "").getTime() - new Date(b.created_at || "").getTime();
      case "most-voted":
        return (b.votes || 0) - (a.votes || 0);
      case "least-voted":
        return (a.votes || 0) - (b.votes || 0);
      default:
        return 0;
    }
  });

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Feedback</h2>
      </div>

      {/* Main content area with grid layout */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Left column (3/4 width on large screens): Search, Filters, and Feedback List */}
        <div className="lg:col-span-3 space-y-6">
          {/* Search and filters row */}
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
            <div className="w-full sm:w-64">
              <FeedbackSearch
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
              />
            </div>
            <div className="flex items-center gap-4 flex-wrap">
              <FeedbackStatusFilter
                selectedStatuses={selectedStatuses}
                setSelectedStatuses={setSelectedStatuses}
                companyStatuses={companyStatuses}
              />
              <FeedbackSortOptions
                sortOption={sortOption}
                setSortOption={setSortOption}
              />
            </div>
          </div>

          {/* Feedback list */}
          <FeedbackList 
            feedback={sortedFeedback} 
            companyStatuses={companyStatuses}
            onVote={onVote} 
          />

          {sortedFeedback.length === 0 && (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No feedback found matching your filters.</p>
            </div>
          )}
        </div>

        {/* Right column (1/4 width on large screens): Board Selector */}
        <div className="lg:col-span-1">
          <BoardSelector
            companyId={companyId}
            selectedBoardId={selectedBoardId}
            onBoardSelect={setSelectedBoardId}
          />
        </div>
      </div>
    </div>
  );
};
